import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Helper to get guest cart from localStorage
const getGuestCart = () => {
  try {
    const saved = localStorage.getItem('guest_cart');
    return saved ? JSON.parse(saved) : { items: [], total: 0 };
  } catch {
    return { items: [], total: 0 };
  }
};

// Helper to save guest cart to localStorage
const saveGuestCart = (cart) => {
  localStorage.setItem('guest_cart', JSON.stringify(cart));
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [guestCart, setGuestCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  // Load guest cart on mount
  useEffect(() => {
    setGuestCart(getGuestCart());
  }, []);

  const fetchCart = useCallback(async () => {
    if (!user) {
      setCart({ items: [], total: 0 });
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/cart`, { withCredentials: true });
      setCart(data);
    } catch {
      setCart({ items: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Fetch product details for guest cart
  const fetchProductDetails = async (productId) => {
    try {
      const { data } = await axios.get(`${API}/products/${productId}`);
      return data;
    } catch {
      return null;
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    if (user) {
      // Authenticated user - use API
      try {
        await axios.post(`${API}/cart/add`, { product_id: productId, quantity }, { withCredentials: true });
        await fetchCart();
        return { success: true };
      } catch (e) {
        return { success: false, error: e.response?.data?.detail || 'Failed to add to cart' };
      }
    } else {
      // Guest user - use localStorage
      try {
        const product = await fetchProductDetails(productId);
        if (!product) {
          return { success: false, error: 'Product not found' };
        }

        const currentCart = getGuestCart();
        const existingIndex = currentCart.items.findIndex(item => item.product_id === productId);

        if (existingIndex >= 0) {
          currentCart.items[existingIndex].quantity += quantity;
          currentCart.items[existingIndex].subtotal = currentCart.items[existingIndex].quantity * product.price;
        } else {
          currentCart.items.push({
            product_id: productId,
            name: product.name,
            price: product.price,
            quantity: quantity,
            image_url: product.image_url,
            subtotal: product.price * quantity
          });
        }

        currentCart.total = currentCart.items.reduce((sum, item) => sum + item.subtotal, 0);
        saveGuestCart(currentCart);
        setGuestCart(currentCart);
        return { success: true };
      } catch (e) {
        return { success: false, error: 'Failed to add to cart' };
      }
    }
  };

  const updateCartItem = async (productId, quantity) => {
    if (user) {
      try {
        await axios.put(`${API}/cart/update`, { product_id: productId, quantity }, { withCredentials: true });
        await fetchCart();
        return { success: true };
      } catch (e) {
        return { success: false, error: e.response?.data?.detail || 'Failed to update cart' };
      }
    } else {
      // Guest cart update
      const currentCart = getGuestCart();
      if (quantity <= 0) {
        currentCart.items = currentCart.items.filter(item => item.product_id !== productId);
      } else {
        const item = currentCart.items.find(item => item.product_id === productId);
        if (item) {
          item.quantity = quantity;
          item.subtotal = item.price * quantity;
        }
      }
      currentCart.total = currentCart.items.reduce((sum, item) => sum + item.subtotal, 0);
      saveGuestCart(currentCart);
      setGuestCart(currentCart);
      return { success: true };
    }
  };

  const removeFromCart = async (productId) => {
    if (user) {
      try {
        await axios.delete(`${API}/cart/remove/${productId}`, { withCredentials: true });
        await fetchCart();
        return { success: true };
      } catch (e) {
        return { success: false, error: e.response?.data?.detail || 'Failed to remove from cart' };
      }
    } else {
      const currentCart = getGuestCart();
      currentCart.items = currentCart.items.filter(item => item.product_id !== productId);
      currentCart.total = currentCart.items.reduce((sum, item) => sum + item.subtotal, 0);
      saveGuestCart(currentCart);
      setGuestCart(currentCart);
      return { success: true };
    }
  };

  const clearCart = async () => {
    if (user) {
      try {
        await axios.delete(`${API}/cart/clear`, { withCredentials: true });
        await fetchCart();
        return { success: true };
      } catch (e) {
        return { success: false, error: e.response?.data?.detail || 'Failed to clear cart' };
      }
    } else {
      const emptyCart = { items: [], total: 0 };
      saveGuestCart(emptyCart);
      setGuestCart(emptyCart);
      return { success: true };
    }
  };

  // Use appropriate cart based on auth state
  const activeCart = user ? cart : guestCart;
  const cartCount = activeCart.items.reduce((acc, item) => acc + item.quantity, 0);

  return (
    <CartContext.Provider value={{ 
      cart: activeCart, 
      loading, 
      cartCount, 
      addToCart, 
      updateCartItem, 
      removeFromCart, 
      clearCart, 
      fetchCart 
    }}>
      {children}
    </CartContext.Provider>
  );
};
